import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { testApiConfig } from './config/api';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import CreateProgram from './components/CreateProgram';
import EditProgram from './components/EditProgram';
import Train from './components/training/train';
import ExecutionLogger from './components/training/ExecutionLogger';
import PrivateRoute from './components/PrivateRoute';
import SessionGuard from './components/SessionGuard';
import Home from './components/Home';
import ExerciseDatabase from './components/ExerciseDatabase';
import PhaseGridPage from './components/PhaseGridPage';
import WeekBuildPage from './components/WeekBuildPage';
import Coaching from './components/Coaching';
import MyTraining from './components/MyTraining';
import MyTrainingHome from './components/MyTrainingHome';
import SelfProgramPage from './components/SelfProgramPage';
import TrainingHistoryPage from './components/TrainingHistoryPage';
import LandingPage from './components/LandingPage';
import UpdateBanner from './components/UpdateBanner';
import ScrollToTop from './components/ScrollToTop';
import SyncStatus from './components/SyncStatus';
import './styles/FloatingBanners.css';


//CHECK IF API_URL IS SET
if (!process.env.REACT_APP_API_URL) {
  console.warn('REACT_APP_API_URL is not set in the environment variables');
}

function App() {
  // Test the API configuration
  const isConfigValid = testApiConfig();
  
  if (!isConfigValid) {
    console.error('API configuration is invalid!');
  }
  
  return (
    <AuthProvider>
      <Router>
        {/* Inside the Router so it can navigate; catches every authenticated
            request's 401 in one place and signs the athlete out cleanly. */}
        <SessionGuard />
        {/* Each new screen opens at the top (see ScrollToTop). */}
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Private Routes */}
          <Route path="/home" element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          } />
          <Route path="/exercises" element={
            <PrivateRoute>
              <ExerciseDatabase />
            </PrivateRoute>
          } />
          <Route path="/create-program" element={
            <PrivateRoute>
              <CreateProgram />
            </PrivateRoute>
          } />
          <Route path="/programs/:programId/edit" element={
            <PrivateRoute>
              <EditProgram />
            </PrivateRoute>
          } />
          <Route path="/training" element={
            <PrivateRoute>
              <MyTrainingHome />
            </PrivateRoute>
          } />
          <Route path="/my-history" element={
            <PrivateRoute>
              <TrainingHistoryPage />
            </PrivateRoute>
          } />
          <Route path="/write-me-a-program" element={
            <PrivateRoute>
              <SelfProgramPage />
            </PrivateRoute>
          } />
          <Route path="/my-training" element={
            <PrivateRoute>
              <MyTraining />
            </PrivateRoute>
          } />
          <Route path="/coaching" element={
            <PrivateRoute>
              <Coaching />
            </PrivateRoute>
          } />
          <Route path="/programs/:programId/phases/:phaseId/grid" element={
            <PrivateRoute>
              <PhaseGridPage />
            </PrivateRoute>
          } />
          {/* The two grids read different axes: the phase grid across WEEKS for
              one exercise, the week view across DAYS within one week. A coach
              writing a block needs both, and only the second carries structural
              balance. */}
          <Route path="/programs/:programId/phases/:phaseId/week" element={
            <PrivateRoute>
              <WeekBuildPage />
            </PrivateRoute>
          } />
          {/* The circuit logger is what an athlete trains from. The older
              flat-list screen stays mounted at /log for now — it carries the
              history view and the bulk-save path, neither of which this
              replaces yet. */}
          <Route path="/programs/:programId/sessions/:sessionId/train" element={
            <PrivateRoute>
              <ExecutionLogger />
            </PrivateRoute>
          } />
          <Route path="/programs/:programId/sessions/:sessionId/log" element={
            <PrivateRoute>
              <Train />
            </PrivateRoute>
          } />

          {/* Catch-all redirect to home for authenticated users or landing for non-authenticated */}
          <Route path="*" element={
            <PrivateRoute>
              <Navigate to="/home" replace />
            </PrivateRoute>
          } />
        </Routes>
        {/* A shared fixed-position stack (see FloatingBanners.css) so the two
            banners below stack instead of overlapping when both are showing
            at once. */}
        <div className="floating-banners">
          {/* Offers a new version when one is waiting. Inside the Router because
              it hides itself on the logging screens (updatePrompt.offersUpdate). */}
          <UpdateBanner />
          {/* Sends sets logged offline once there is a network, from any screen,
              and says how many are waiting. Hidden on the logging screens, whose
              header carries the count. */}
          <SyncStatus />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
